import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePublicConfig } from "@/lib/publicConfig";

/**
 * Games disponibles en HitTCG. Los "coming soon" aparecen en el modal
 * de bienvenida como opciones marcables (para saber qué priorizar), pero
 * NO están activos en catálogo/publicar hasta que se agreguen.
 */
export const GAMES = {
  pokemon: {
    key: "pokemon",
    label: "Pokémon",
    color: "#EF4444",
    bg: "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/40",
    active: true,
  },
  riftbound: {
    key: "riftbound",
    label: "Riftbound",
    color: "#3B82F6",
    bg: "bg-blue-500/10 text-blue-300 border-blue-500/40",
    active: true,
  },
  yugioh: { key: "yugioh", label: "Yu-Gi-Oh!", color: "#a855f7", bg: "bg-purple-500/10 text-purple-300 border-purple-500/40", active: false },
  magic: { key: "magic", label: "Magic: The Gathering", color: "#f59e0b", bg: "bg-amber-500/10 text-amber-300 border-amber-500/40", active: false },
  onepiece: { key: "onepiece", label: "One Piece TCG", color: "#f97316", bg: "bg-orange-500/10 text-orange-300 border-orange-500/40", active: false },
};

// `ACTIVE_GAMES` es un snapshot estático (default) — Pokémon + Riftbound.
// Yu-Gi-Oh se activa por entorno vía el flag backend `yugioh_enabled` en
// `/config/public`. Usa `useActiveGames()` dentro de componentes para la
// lista reactiva al config público (preview ON / prod OFF).
export const ACTIVE_GAMES = Object.values(GAMES).filter((g) => g.active);

export function useActiveGames() {
  const cfg = usePublicConfig();
  return useMemo(() => {
    const base = Object.values(GAMES).filter((g) => g.active);
    if (cfg?.yugioh_enabled && !base.find((g) => g.key === "yugioh")) {
      base.push({ ...GAMES.yugioh, active: true });
    }
    return base;
  }, [cfg?.yugioh_enabled]);
}

const LS_KEY = "hittcg_active_game";
const DEFAULT_GAME = "pokemon";

const GameContext = createContext(null);

export function GameProvider({ children }) {
  const { user } = useAuth();
  // Preferencia visible del usuario para navegar (persistida en localStorage
  // para preservar la selección al recargar; se inicializa con la preferencia
  // del backend cuando el usuario se loguea).
  const [activeGame, setActiveGameRaw] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      return stored && GAMES[stored] ? stored : DEFAULT_GAME;
    } catch { return DEFAULT_GAME; }
  });
  const [prefs, setPrefs] = useState(null); // { primary_game, games_of_interest, welcome_seen_at }

  const setActiveGame = useCallback((g) => {
    if (!GAMES[g]) return;
    setActiveGameRaw(g);
    try { localStorage.setItem(LS_KEY, g); } catch { /* private mode */ }
  }, []);

  // Cargar preferencias del backend cuando hay usuario logueado. Si el
  // usuario tiene un `primary_game` sincronizamos el activeGame apenas
  // llegue el fetch (una vez por login).
  useEffect(() => {
    if (!user) { setPrefs(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/me/game-preferences");
        if (cancelled) return;
        setPrefs(r.data);
        // Si el usuario nunca eligió activo manualmente, respetar primary_game.
        try {
          const stored = localStorage.getItem(LS_KEY);
          if (!stored && r.data?.primary_game && GAMES[r.data.primary_game]) {
            setActiveGameRaw(r.data.primary_game);
          }
        } catch { /* ignore */ }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const refreshPrefs = useCallback(async () => {
    if (!user) return null;
    try {
      const r = await api.get("/me/game-preferences");
      setPrefs(r.data);
      return r.data;
    } catch { return null; }
  }, [user]);

  return (
    <GameContext.Provider value={{ activeGame, setActiveGame, prefs, refreshPrefs, GAMES, ACTIVE_GAMES }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used inside <GameProvider>");
  return ctx;
}

/**
 * Inferir a qué juego pertenece una carta a partir de su documento o del
 * card_id. Prioridad:
 *   1. Campo `game` explícito (asignado por el backend con la regla dura)
 *   2. Campo `game_slug` del card doc
 *   3. Formato del ID como último fallback (3+ segmentos = Riftbound)
 * Nunca dropear una carta por su formato de ID si el backend ya decidió.
 */
export function inferGameOfCard(cardOrId) {
  const isObj = typeof cardOrId === "object" && cardOrId !== null;
  const explicit = isObj ? cardOrId.game : null;
  if (explicit && GAMES[explicit]) return explicit;
  const slug = isObj ? cardOrId.game_slug : null;
  if (slug && GAMES[slug]) return slug;
  const id = typeof cardOrId === "string" ? cardOrId : (isObj ? (cardOrId.card_id || cardOrId.id) : null);
  if (!id) return "pokemon";
  const parts = String(id).split("-");
  if (/^\d{5,10}$/.test(String(id))) return "yugioh";
  return parts.length >= 3 ? "riftbound" : "pokemon";
}
